import { UserSettingsProjectState } from '@models/user-settings';
import Collapse from '@mui/material/Collapse';
import ListSubheader from '@mui/material/ListSubheader';
import Typography from '@mui/material/Typography';
import { typedMemo } from '@utils';
import { useTranslation } from 'react-i18next';
import { TransitionGroup } from 'react-transition-group';
import { ProjectMenuItem } from './ProjectMenuItem';
import { ProjectMenuItemSkeleton } from './ProjectMenuItemSkeleton';
import styles from './ProjectsList.module.scss';

interface ProjectsListProps {
  projects: UserSettingsProjectState[];
  showSkeleton: boolean;
  onProjectSelect: (project: UserSettingsProjectState) => void;
  onProjectPin: (project: UserSettingsProjectState, shouldPin: boolean) => void;
  isPinned: (project: UserSettingsProjectState) => boolean;
  listType: 'pinned' | 'recent';
  canPin?: boolean; // Only needed for pinned lists
  currentProjectId?: string;
}

export const ProjectsList = typedMemo(
  ({
    projects,
    showSkeleton,
    onProjectSelect,
    onProjectPin,
    isPinned,
    listType,
    canPin: canPinProp,
    currentProjectId,
  }: Readonly<ProjectsListProps>) => {
    const { t } = useTranslation();

    const canPin = canPinProp ?? listType === 'pinned';
    const titleKey =
      listType === 'pinned'
        ? 'projectSelector.pinnedProjects'
        : 'projectSelector.recentProjects';
    const showWhenEmpty = listType === 'recent';

    const isVisible = showWhenEmpty || projects.length > 0 || showSkeleton;

    let content;
    if (showSkeleton) {
      content = <ProjectMenuItemSkeleton />;
    } else if (projects.length > 0) {
      content = (
        <TransitionGroup component={null}>
          {projects.map((project: UserSettingsProjectState) => (
            <Collapse
              key={project.projektstadiumId}
              timeout={300}
              unmountOnExit
            >
              <ProjectMenuItem
                project={project}
                onProjectSelect={onProjectSelect}
                onProjectPin={onProjectPin}
                isPinned={isPinned(project)}
                canPin={canPin}
                listType={listType}
                isCurrent={project.projektstadiumId === currentProjectId}
              />
            </Collapse>
          ))}
        </TransitionGroup>
      );
    } else if (isVisible) {
      content = (
        <div
          className={styles.emptyState}
          style={{
            minHeight: '120px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('projectSelector.noRecentProjects')}
          </Typography>
        </div>
      );
    }

    return (
      <Collapse in={isVisible} timeout={300} unmountOnExit>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <ListSubheader data-testid={`project-list-header-${listType}`}>
            {t(titleKey)}
          </ListSubheader>
          <div
            className="small-scrollbar"
            style={{ minHeight: '44px', maxHeight: '290px', overflowY: 'auto' }}
          >
            {content}
          </div>
        </div>
      </Collapse>
    );
  },
);
